import React, { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import SummaryItem from '@components/ssap/pages/pageIncludes/summary/SummaryItem';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { DATE_FORMAT } from '@utils/dates/dateMethods';

// Types

import { History } from 'history';
import {
    MemberRemovalReason,
    RemovedMember,
    RemovedMembers,
} from '../../../../../ssap/SsapTypes';
import { dayjsUtil, toUTCAndFormat } from '@utils/dates/date.utils';

interface RemovedMembersSummaryPropTypes {
    ButtonContainer: Function;
    history: History;
    removedMembers: RemovedMembers;
}

const RemovedMembersSummary = (props: RemovedMembersSummaryPropTypes): ReactElement => {
    const {
        ButtonContainer,
        history,
        removedMembers
    } = props;

    const getRemovedMembers = (): ReactElement[] => {

        const getFormattedDate = (date: string | null): string => toUTCAndFormat(date || dayjsUtil(), DATE_FORMAT);

        return Object.entries(removedMembers).map((entry: [string, RemovedMember]): ReactElement => {
            const [
                applicantGuid,
                {
                    demographic: {
                        birthDate,
                        deathDate,
                        divorceDate,
                        name
                    },
                    removalReasonType
                }
            ] = entry;

            type IgnoreIntl = [ number, number] | [ number ];
            type Payload = [ string, string ] | [];

            type ReasonAndDate = {
                ignoreIntl: IgnoreIntl,
                payload: Payload
            }

            const getReasonAndDate = (): ReasonAndDate => {
                let ignoreIntl: IgnoreIntl = [ 0, 2 ];
                let payload: Payload = [];

                switch(removalReasonType) {
                    case MemberRemovalReason.DEATH:
                        payload = [
                            'ssap.input.option.death',
                            getFormattedDate(deathDate)
                        ];
                        break;
                    case MemberRemovalReason.DIVORCE:
                        payload = [
                            'ssap.input.option.divorce',
                            getFormattedDate(divorceDate)
                        ];
                        break;
                    case MemberRemovalReason.OTHER:
                        ignoreIntl = [ 0 ];
                        payload = [
                            'ssap.input.option.other',
                            'common.not_applicable'
                        ];
                        break;
                    default:
                        ignoreIntl = [ 0 ];
                        payload = [
                            'common.not_applicable',
                            'common.not_applicable'
                        ];
                }

                return {
                    ignoreIntl,
                    payload
                };
            };

            const { ignoreIntl, payload } = getReasonAndDate();

            return (
                <SummaryItem
                    key = { applicantGuid }
                    category = { normalizeNameOfPerson(name) }
                    textCode = ''
                    value = { [
                        getFormattedDate(birthDate),
                        ...payload
                    ] }
                    ignoreIntl = { ignoreIntl }
                    gridForFourAllEqualClasses
                />
            );
        });
    };

    return (
        <div className = 'summary__block margin-b-20 gi-table__responsive' id = 'removedMembersSummary' >
            <div className = 'summary__header'>
                <div className = 'grid-row'>
                    <h4 className = 'mobile:grid-col-12 tablet:grid-col-fill'>
                        <FormattedMessage
                            id = 'ssap.subheader.removed_household_members'
                        />
                    </h4>
                    <div className = 'mobile:grid-col-2 tablet:grid-col-auto float-right'>
                        {
                            <ButtonContainer
                                args = { [ 'undoMemberRemoval' ] }
                                customButtonClassName = 'btn-small'
                                history = { history }
                                type = 'editButton_secondary'
                            />
                        }
                    </div>
                </div>
            </div>
            <table className = 'usa-table usa-table--borderless gi-table gi-table__borderless' >
                <thead>
                    <SummaryItem
                        renderAs = 'th'
                        textCode = 'common.name'
                        value = { [ 'common.date_of_birth', 'common.reason_for_removal', 'common.date_of_removal' ] }
                        gridForFourAllEqualClasses
                    />
                </thead>
                <tbody>
                    {
                        getRemovedMembers()
                    }
                </tbody>
            </table>
        </div>
    );
};

export default RemovedMembersSummary;